import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import { Eye, Pencil, Trash2, Search, X, Lock } from "lucide-react";
import api from "../api/axios";

const BorderTax = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [viewItem, setViewItem] = useState(null);
  const [loading, setLoading] = useState(true);


  const ITEMS_PER_PAGE = 8;

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/border-tax?isAdmin=true`);
      if (res.data?.success) {
        setData(res.data.data || []);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      Swal.fire({ icon: "error", title: "Oops...", text: "Failed to load data" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredData = data.filter((item) =>
    item.state?.toLowerCase().includes(search.toLowerCase().trim())
  );

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedData = filteredData.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const handleDelete = async (item) => {
    if (!item.canEdit) {
      Swal.fire({
        icon: "warning",
        title: "Not allowed",
        text: "You don't have permission to delete this entry.",
      });
      return;
    }

    const result = await Swal.fire({
      title: "Confirm Deletion",
      text: `Delete border tax for "${item.state}"? This cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Delete",
    });

    if (!result.isConfirmed) return;

    try {
      await api.delete(`/admin/border-tax/${item._id}`);
      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: "Deleted successfully",
        showConfirmButton: false,
        timer: 2200,
      });
      fetchData();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Delete failed",
        text: error?.response?.data?.message || "",
      });
    }
  };

  const toggleStateStatus = async (item) => {

    const newStatus = !item.active;

    try {
      await api.put(`/admin/border-tax/${item._id}`, { active: newStatus });

      setData((prev) =>
        prev.map((s) => (s._id === item._id ? { ...s, active: newStatus } : s))
      );

      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "success",
        title: `State ${newStatus ? "Activated" : "Deactivated"}`,
        showConfirmButton: false,
        timer: 2000,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Update failed",
        text: err?.response?.data?.message || "",
      });
    }
  };

  const handleEdit = (item) => {
    if (!item.canEdit) {
      Swal.fire({
        icon: "warning",
        title: "View only",
        text: "You can only view this entry, not edit it.",
      });
      return;
    }
    navigate(`/border-tax/edit/${item._id}`);
  };

  const getPageNumbers = () => {
    const max = 5;
    const half = Math.floor(max / 2);
    let start = Math.max(1, currentPage - half);
    let end = Math.min(totalPages, start + max - 1);
    if (end - start + 1 < max) start = Math.max(1, end - max + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const formatPlatformFee = (platform) => {
    if (!platform || !platform.is_fee_apply) return "Disabled";
    const fee = platform.fee ? `₹${platform.fee}` : "—";
    const gateway = platform.gateway_fee ? `${platform.gateway_fee}%` : "—";
    return `${fee} + ${gateway}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
            Border Tax Management
          </h1>
          {/* <button
            onClick={() => navigate("/border-tax/create")}
            className="inline-flex items-center px-4 py-2.5 bg-indigo-600 text-white rounded-lg shadow hover:bg-indigo-700 transition font-medium"
          >
            + Add New State Tax
          </button> */}
        </div>

        <div className="mb-6 relative max-w-md">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by state..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="bg-white shadow-sm rounded-xl overflow-hidden border border-gray-200">
          {loading ? (
            <div className="p-12 text-center text-gray-500">
              <div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              Loading...
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">State</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Cab Price Rules</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Border Checkpoints</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Platform Fee</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">On / Off</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {paginatedData.map((item) => {
                      const borders = item?.borderEntries || [];
                      const showBorders = borders.length > 3 ? borders.slice(0, 3) : borders;
                      const remaining = borders.length - 3;

                      return (
                        <tr key={item._id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">{item.state}</span>
                              {!item.canEdit && (
                                <Lock size={14} className="text-gray-400" title="View only" />
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center text-sm text-gray-600">
                            {item.pricesByDays?.length || 0}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-wrap gap-1.5 justify-center">
                              {showBorders.map((b, i) => (
                                <span
                                  key={i}
                                  className={`text-xs px-2 py-0.5 rounded-full border ${b.isActive
                                    ? "bg-green-50 text-green-700 border-green-200"
                                    : "bg-red-50 text-red-700 border-red-200"
                                    }`}
                                >
                                  {b.title}
                                </span>
                              ))}
                              {remaining > 0 && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                                  +{remaining}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-center text-sm text-gray-600">
                            {formatPlatformFee(item.platform)}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <button
                              onClick={() => toggleStateStatus(item)}

                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition disabled:opacity-40 disabled:cursor-not-allowed ${item.active ? "bg-green-500" : "bg-gray-300"
                                }`}
                            >
                              <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${item.active ? "translate-x-6" : "translate-x-1"
                                  }`}
                              />
                            </button>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2 justify-center">
                              <button
                                onClick={() => setViewItem(item)}
                                title="View"
                                className="p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50"
                              >
                                <Eye size={16} />
                              </button>
                              <button
                                onClick={() => handleEdit(item)}
                                title={item.canEdit ? "Edit" : "View only"}
                                className={`p-1.5 rounded-lg ${item.canEdit
                                  ? "text-amber-600 hover:bg-amber-50"
                                  : "text-gray-300 cursor-not-allowed"
                                  }`}
                              >
                                <Pencil size={16} />
                              </button>
                              <button
                                onClick={() => handleDelete(item)}
                                title={item.canEdit ? "Delete" : "View only"}
                                className={`p-1.5 rounded-lg ${item.canEdit
                                  ? "text-red-600 hover:bg-red-50"
                                  : "text-gray-300 cursor-not-allowed"
                                  }`}
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <p className="text-sm text-gray-600">
                    Showing <span className="font-medium">{startIndex + 1}</span> to{" "}
                    <span className="font-medium">
                      {Math.min(startIndex + ITEMS_PER_PAGE, filteredData.length)}
                    </span>{" "}
                    of <span className="font-medium">{filteredData.length}</span>
                  </p>

                  <nav className="inline-flex -space-x-px rounded-md shadow-sm">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    {getPageNumbers().map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-4 py-2 text-sm font-medium border border-gray-300 ${currentPage === page
                          ? "bg-indigo-600 text-white"
                          : "text-gray-700 hover:bg-gray-50"
                          }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-r-md hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              )}
            </>
          )}
        </div>

        {viewItem && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">
                  {viewItem.state} – Border Tax Details
                </h2>
                <button onClick={() => setViewItem(null)} className="text-gray-500 hover:text-gray-700">
                  <X size={24} />
                </button>
              </div>

              <div className="p-6 space-y-8">
                <section>
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Platform Fee Configuration</h3>
                  <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                      <div>
                        <p className="text-sm text-gray-600">Status</p>
                        <p className="font-medium text-lg mt-1">
                          {viewItem.platform?.is_fee_apply ? (
                            <span className="text-emerald-600">Enabled</span>
                          ) : (
                            <span className="text-rose-600">Disabled</span>
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Flat Fee</p>
                        <p className="font-medium text-lg mt-1">
                          {viewItem.platform?.fee ? `₹${viewItem.platform.fee}` : "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Gateway Fee</p>
                        <p className="font-medium text-lg mt-1">
                          {viewItem.platform?.gateway_fee ? `${viewItem.platform.gateway_fee}%` : "—"}
                        </p>
                      </div>
                    </div>
                  </div>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">
                    Cab Price Rules ({viewItem.pricesByDays?.length || 0})
                  </h3>
                  {viewItem.pricesByDays?.length > 0 ? (
                    <div className="overflow-x-auto border border-gray-200 rounded-lg">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Cab Type</th>
                            <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">After Last Day Increase</th>
                            <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Slabs (Days → Price)</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {viewItem.pricesByDays.map((cab) => (
                            <tr key={cab._id || cab.cabType}>
                              <td className="px-4 py-4 text-sm font-medium text-gray-900 capitalize">{cab.cabType}</td>
                              <td className="px-4 py-4 text-sm text-center text-gray-600">
                                {cab.afterLastDayIncreasePerDayWise ? `₹${cab.afterLastDayIncreasePerDayWise}/day` : "—"}
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-600">
                                <div className="flex flex-wrap gap-2 justify-center">
                                  {cab.slabs.map((slab, i) => (
                                    <span key={i} className="inline-flex items-center px-3 py-1 bg-blue-50 text-blue-800 text-xs font-medium rounded-full">
                                      {slab.days}d → ₹{slab.price}
                                    </span>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-500 italic">No pricing rules added.</p>
                  )}
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">
                    Border Checkpoints ({viewItem.borderEntries?.length || 0})
                  </h3>
                  {viewItem.borderEntries?.length > 0 ? (
                    <div className="flex flex-wrap gap-2.5">
                      {viewItem.borderEntries.map((entry, i) => (
                        <span
                          key={i}
                          className={`inline-flex items-center px-3.5 py-1.5 rounded-full text-sm font-medium border ${entry.isActive
                            ? "bg-green-100 text-green-800 border-green-200"
                            : "bg-red-100 text-red-800 border-red-200"
                            }`}
                        >
                          {entry.title}
                          {!entry.isActive && <span className="ml-1.5 text-xs opacity-80">(inactive)</span>}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 italic">No checkpoints added yet.</p>
                  )}
                </section>
              </div>

              <div className="p-6 border-t border-gray-200 flex justify-end">
                <button
                  onClick={() => setViewItem(null)}
                  className="px-6 py-2.5 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BorderTax;
